/**
 * Poll GET /plan/{id} until complete, then persist AggregatedPlanData.
 * Mirrors iOS CityVisitCard AI plan polling.
 */

import { parseAggregatedPlanData } from '../utils/aggregatedPlanData';
import {
  hydrateCityPlanFromFile,
  persistCityPlanData,
  updateCityAiStatus,
} from './aiPlanPersistService';
import { fetchPlanStatus } from './planningApi';
import { includesAIPlan } from './subscription/subscriptionService';
import { getTrip, getUserIdForTripWrites } from './tripsService';

const DEFAULT_INTERVAL_MS = 4000;
const MAX_POLL_ATTEMPTS = 90;

export interface PollCityPlanOptions {
  tripId: string;
  cityVisitId: string;
  aiJobId: string;
  intervalMs?: number;
  maxAttempts?: number;
  /** Injected for tests */
  sleep?: (ms: number) => Promise<void>;
  signal?: AbortSignal;
  /** Notify UI (e.g. Trip Detail) that storage trip changed so React state can reload. */
  onTripUpdated?: () => void;
}

export type PollCityPlanOutcome =
  | { status: 'done'; planReady: true }
  | { status: 'failed'; reason: string }
  | { status: 'aborted' }
  | { status: 'skipped'; reason: string };

const activePolls = new Map<string, AbortController>();

function pollKey(tripId: string, cityVisitId: string): string {
  return `${tripId}::${cityVisitId}`;
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/** Stop an in-flight poll for a city card. */
export function stopPollingCityPlan(tripId: string, cityVisitId: string): void {
  const key = pollKey(tripId, cityVisitId);
  const ctrl = activePolls.get(key);
  if (ctrl) {
    ctrl.abort();
    activePolls.delete(key);
  }
}

/** Stop all polls (tests / full teardown). Prefer leaving polls running when navigating away. */
export function stopAllCityPlanPolling(): void {
  for (const ctrl of activePolls.values()) {
    ctrl.abort();
  }
  activePolls.clear();
}

/**
 * Poll until the plan job is complete (or failed / aborted).
 * Idempotent per city: starts a new poll only if none is running.
 */
export async function pollCityPlanUntilReady(
  options: PollCityPlanOptions
): Promise<PollCityPlanOutcome> {
  if (!includesAIPlan()) {
    return { status: 'skipped', reason: 'no-subscription' };
  }
  const jobId = options.aiJobId.trim();
  if (!jobId) {
    return { status: 'skipped', reason: 'no-job-id' };
  }

  const key = pollKey(options.tripId, options.cityVisitId);
  if (activePolls.has(key)) {
    return { status: 'skipped', reason: 'already-polling' };
  }
  const ctrl = new AbortController();
  if (options.signal) {
    if (options.signal.aborted) {
      return { status: 'aborted' };
    }
    options.signal.addEventListener('abort', () => ctrl.abort(), { once: true });
  }
  activePolls.set(key, ctrl);

  const sleep = options.sleep ?? defaultSleep;
  const intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
  const maxAttempts = options.maxAttempts ?? MAX_POLL_ATTEMPTS;
  const notify = (): void => {
    options.onTripUpdated?.();
  };

  try {
    // Already have local plan?
    const userId = getUserIdForTripWrites();
    if (userId) {
      const trip = getTrip(userId, options.tripId);
      if (trip?.aiPlanDataByCity?.[options.cityVisitId]) {
        notify();
        return { status: 'done', planReady: true };
      }
      const hydrated = await hydrateCityPlanFromFile(
        options.tripId,
        options.cityVisitId
      );
      if (hydrated) {
        notify();
        return { status: 'done', planReady: true };
      }
    }

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      if (ctrl.signal.aborted) {
        return { status: 'aborted' };
      }
      if (!navigator.onLine) {
        updateCityAiStatus(options.tripId, options.cityVisitId, 'queued');
        notify();
        await sleep(intervalMs);
        continue;
      }

      const result = await fetchPlanStatus(jobId);
      if (ctrl.signal.aborted) {
        return { status: 'aborted' };
      }

      if (!result.ok && result.jobStatus === 'failed') {
        updateCityAiStatus(options.tripId, options.cityVisitId, 'failed');
        notify();
        return {
          status: 'failed',
          reason: result.errorMessage || `HTTP ${result.status}`,
        };
      }

      if (result.isComplete && result.result) {
        const plan = parseAggregatedPlanData(result.result);
        if (!plan) {
          updateCityAiStatus(options.tripId, options.cityVisitId, 'failed');
          notify();
          return { status: 'failed', reason: 'parse-failed' };
        }
        await persistCityPlanData({
          tripId: options.tripId,
          cityVisitId: options.cityVisitId,
          plan,
        });
        notify();
        return { status: 'done', planReady: true };
      }

      const midStatus =
        result.jobStatus === 'processing' || result.jobStatus === 'running'
          ? 'running'
          : result.jobStatus === 'complete'
            ? 'done'
            : 'queued';
      updateCityAiStatus(options.tripId, options.cityVisitId, midStatus);
      notify();
      await sleep(intervalMs);
    }

    updateCityAiStatus(options.tripId, options.cityVisitId, 'failed');
    notify();
    return { status: 'failed', reason: 'timeout' };
  } finally {
    if (activePolls.get(key) === ctrl) {
      activePolls.delete(key);
    }
  }
}

/**
 * Start polling for every city visit that has an aiJobId and no ready plan.
 * Call from Trip Detail on mount.
 */
export function startPollingTripPlans(
  tripId: string,
  options?: { onTripUpdated?: () => void }
): void {
  if (!includesAIPlan()) return;
  const userId = getUserIdForTripWrites();
  if (!userId) return;
  const trip = getTrip(userId, tripId);
  if (!trip) return;

  for (const cityVisitId of trip.cityIds ?? []) {
    const meta = trip.cityVisitMeta?.[cityVisitId];
    const jobId = meta?.aiJobId?.trim();
    if (!jobId) continue;
    if (trip.aiPlanDataByCity?.[cityVisitId]) continue;
    if (meta?.aiStatus === 'failed') continue;
    void pollCityPlanUntilReady({
      tripId,
      cityVisitId,
      aiJobId: jobId,
      onTripUpdated: options?.onTripUpdated,
    });
  }
}
